import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard } from '../api/client';

interface Alert {
  id: number;
  manager_name: string;
  message: string;
  severity: string;
  created_at: string;
  dialogue_id?: number;
}

interface ManagerCard {
  id: number;
  name: string;
  current_score: number;
  score_trend: number;
  total_dialogues: number;
  key_issues: string[];
}

interface DashboardData {
  alerts: Alert[];
  managers: ManagerCard[];
}

type FilterType = 'all' | 'critical' | 'warning';

const getInitials = (name: string) => {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const AlertCard: React.FC<{ alert: Alert; onDetails: (id: number) => void }> = ({ alert, onDetails }) => {
  const [hovered, setHovered] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);
  const isCritical = alert.severity === 'critical';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '18px 20px',
        border: `1px solid ${isCritical ? '#fecaca' : '#fde68a'}`,
        boxShadow: hovered ? '0 8px 25px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        transition: 'box-shadow 0.2s ease',
      }}
    >
      {/* Severity dot */}
      <div style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        background: isCritical ? '#ef4444' : '#f59e0b',
        flexShrink: 0,
        marginTop: 4,
        boxShadow: isCritical ? '0 0 6px rgba(239,68,68,0.4)' : '0 0 6px rgba(245,158,11,0.4)',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{alert.manager_name}</div>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 20,
            background: isCritical ? '#fef2f2' : '#fffbeb',
            color: isCritical ? '#dc2626' : '#d97706',
            flexShrink: 0,
          }}>
            {isCritical ? 'Критично' : 'Предупреждение'}
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#475569', marginTop: 5, lineHeight: 1.5 }}>{alert.message}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            {new Date(alert.created_at).toLocaleString('ru-RU')}
          </div>
          {alert.dialogue_id && (
            <button
              onClick={() => onDetails(alert.dialogue_id!)}
              onMouseEnter={() => setBtnHovered(true)}
              onMouseLeave={() => setBtnHovered(false)}
              style={{
                fontSize: 12,
                fontWeight: 500,
                padding: '5px 12px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                background: btnHovered ? '#6366f1' : '#fff',
                color: btnHovered ? '#fff' : '#6366f1',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Подробнее
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProblemManagerCard: React.FC<{ m: ManagerCard; onClick: () => void }> = ({ m, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 16,
        border: '1px solid #fecaca',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hovered ? '0 8px 25px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: '#ef4444',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        color: '#fff',
        flexShrink: 0,
      }}>
        {getInitials(m.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{m.name}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{m.total_dialogues} диалогов</div>
      </div>
      <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>{m.current_score}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>/ 100</div>
      </div>
    </div>
  );
};

const Problems: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const navigate = useNavigate();

  useEffect(() => {
    getDashboard()
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 15, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      Загрузка...
    </div>
  );

  if (!data) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#ef4444', fontSize: 15, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      Ошибка загрузки
    </div>
  );

  const alerts: Alert[] = data.alerts || [];
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity !== 'critical').length;

  const filteredAlerts = alerts.filter(a => {
    if (filter === 'critical') return a.severity === 'critical';
    if (filter === 'warning') return a.severity !== 'critical';
    return true;
  });

  const problemManagers = (data.managers || []).filter(m => m.current_score < 60);

  const metrics = [
    {
      label: 'Критических',
      value: criticalCount,
      icon: '🔴',
      valueColor: criticalCount > 0 ? '#ef4444' : '#10b981',
    },
    {
      label: 'Предупреждений',
      value: warningCount,
      icon: '🟡',
      valueColor: warningCount > 0 ? '#f59e0b' : '#10b981',
    },
    {
      label: 'Всего за неделю',
      value: alerts.length,
      icon: '📋',
      valueColor: alerts.length > 0 ? '#6366f1' : '#10b981',
    },
  ];

  const filterButtons: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'Все' },
    { key: 'critical', label: 'Критические' },
    { key: 'warning', label: 'Предупреждения' },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={styles.title}>Проблемы</h1>
        <p style={styles.subtitle}>Алерты и нарушения</p>
      </div>

      {/* Metrics */}
      <div style={styles.metricsGrid}>
        {metrics.map((m, i) => (
          <div key={i} style={styles.metricCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={styles.metricLabel}>{m.label}</div>
              <span style={{ fontSize: 20 }}>{m.icon}</span>
            </div>
            <div style={{ ...styles.metricValue, color: m.valueColor }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Active Alerts */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' as const, gap: 12 }}>
          <h3 style={styles.sectionTitle}>Активные алерты</h3>
          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 8 }}>
            {filterButtons.map(btn => (
              <FilterPill
                key={btn.key}
                label={btn.label}
                active={filter === btn.key}
                onClick={() => setFilter(btn.key)}
              />
            ))}
          </div>
        </div>

        {filteredAlerts.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={{ fontSize: 32, marginBottom: 8, display: 'block' }}>✅</span>
            Алертов нет
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
            {filteredAlerts.map(alert => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onDetails={(dialogueId) => navigate(`/dialogue/${dialogueId}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Problem Managers */}
      <div>
        <h3 style={styles.sectionTitle}>Проблемные диалоги</h3>
        <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 16px 0' }}>Менеджеры с оценкой ниже 60</p>
        {problemManagers.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={{ fontSize: 32, marginBottom: 8, display: 'block' }}>🏆</span>
            Все менеджеры в норме
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {problemManagers.map(m => (
              <ProblemManagerCard
                key={m.id}
                m={m}
                onClick={() => navigate(`/manager/${m.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const FilterPill: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '6px 14px',
        borderRadius: 20,
        border: active ? '1px solid #6366f1' : '1px solid #e2e8f0',
        background: active ? '#6366f1' : (hovered ? '#f8fafc' : '#fff'),
        color: active ? '#fff' : (hovered ? '#6366f1' : '#64748b'),
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {label}
    </button>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px 36px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  subtitle: {
    fontSize: 15,
    color: '#94a3b8',
    margin: '4px 0 0 0',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginBottom: 28,
  },
  metricCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  metricLabel: {
    fontSize: 13,
    color: '#94a3b8',
    fontWeight: 500,
    marginBottom: 10,
  },
  metricValue: {
    fontSize: 32,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.5px',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#0f172a',
    margin: 0,
  },
  emptyState: {
    background: '#fff',
    borderRadius: 12,
    padding: '36px 20px',
    border: '1px solid #e2e8f0',
    textAlign: 'center' as const,
    color: '#94a3b8',
    fontSize: 15,
  },
};

export default Problems;
